<template>
  <div class="gsett-body">
    <hs-alert
      v-if="!canManageSettings"
      tone="warning"
      title="Read-only access"
      message="Only group owners and administrators can modify settings."
    />

    <div class="gsett-card">
      <p class="gsett-card__title">Synchronization Settings</p>
      <div class="gsett-checks">
        <hs-checkbox
          v-model="form.sync_products"
          label="Enable Product Sync"
          description="Allow stores in this group to share and sync product catalogs"
          :disabled="!canManageSettings || saving"
        />
        <hs-checkbox
          v-model="form.sync_services"
          label="Enable Service Sync"
          description="Allow stores in this group to share and sync service catalogs"
          :disabled="!canManageSettings || saving"
        />
        <hs-checkbox
          v-model="form.require_sku"
          label="Require SKU for Products"
          description="All products must have a SKU to enable proper product mapping"
          :disabled="!canManageSettings || saving"
        />
      </div>

      <hs-alert v-if="hasChanges" tone="info" message="You have unsaved changes." />
      <hs-alert v-if="error" tone="danger" :message="error" />

      <div v-if="canManageSettings" class="gsett-actions">
        <hs-button
          v-if="hasChanges"
          variant="ghost"
          :disabled="saving"
          @click="resetForm"
        >
          Cancel
        </hs-button>
        <hs-button
          variant="primary"
          icon="save"
          :disabled="!hasChanges"
          :loading="saving"
          @click="saveSettings"
        >
          Save Changes
        </hs-button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, watch } from "vue";
import { useStore } from "vuex";
import { toast } from "vue3-toastify";
import http from "@/helper/http";
import { HsButton, HsCheckbox, HsAlert } from "@/components/ui";

export default {
  name: "GroupSettingsTab",
  components: { HsButton, HsCheckbox, HsAlert },
  props: {
    group: { type: Object, required: true },
  },
  emits: ["refresh"],
  setup(props, { emit }) {
    const store = useStore();
    const saving = ref(false);
    const error = ref("");

    const form = ref({
      sync_products: props.group.settings?.sync_products || false,
      sync_services: props.group.settings?.sync_services || false,
      require_sku: props.group.settings?.require_sku || false,
    });

    const initialSettings = ref({ ...form.value });

    const canManageSettings = computed(() =>
      store.getters["groups/canManageSettings"] || store.getters["groups/isGroupOwner"]
    );

    const hasChanges = computed(() =>
      form.value.sync_products !== initialSettings.value.sync_products ||
      form.value.sync_services !== initialSettings.value.sync_services ||
      form.value.require_sku !== initialSettings.value.require_sku
    );

    const resetForm = () => {
      form.value = { ...initialSettings.value };
      error.value = "";
    };

    const saveSettings = async () => {
      saving.value = true;
      error.value = "";
      try {
        await http.patch(`/v2/groups/${props.group.id}/settings`, {
          sync_products: form.value.sync_products,
          sync_services: form.value.sync_services,
          require_sku: form.value.require_sku,
        });
        toast.success("Group settings updated successfully!");
        initialSettings.value = { ...form.value };
        emit("refresh");
      } catch (err) {
        console.error("Failed to save settings", err);
        error.value = err?.response?.data?.error?.message || "Unable to save settings. Please try again.";
      } finally {
        saving.value = false;
      }
    };

    watch(
      () => props.group.settings,
      (newSettings) => {
        if (newSettings && !hasChanges.value) {
          form.value = {
            sync_products: newSettings.sync_products || false,
            sync_services: newSettings.sync_services || false,
            require_sku: newSettings.require_sku || false,
          };
          initialSettings.value = { ...form.value };
        }
      },
      { deep: true }
    );

    return { saving, error, form, canManageSettings, hasChanges, resetForm, saveSettings };
  },
};
</script>

<style scoped>
.gsett-body {
  display: grid;
  gap: 1.1rem;
}

.gsett-card {
  background: color-mix(in srgb, var(--hs-surface) 92%, transparent);
  border: 1px solid var(--hs-border);
  border-radius: 14px;
  display: grid;
  gap: 1rem;
  padding: 1.25rem;
}

.gsett-card__title {
  color: var(--hs-text);
  font-size: 0.9rem;
  font-weight: 750;
  margin: 0;
}

.gsett-checks {
  display: grid;
  gap: 0.85rem;
}

.gsett-actions {
  align-items: center;
  display: flex;
  gap: 0.6rem;
  justify-content: flex-end;
  padding-top: 0.25rem;
}
</style>
